"use client";

/**
 * Live Prices on Sign-In (2026-08-23): the freshness ribbon.
 *
 * Shown ONLY to a signed-out visitor on a stale (>24h) saved page. The full
 * comparison stays visible beneath it (this is an invitation, never a wall), so
 * Google and bots keep the whole indexed page. The button opens the existing
 * sign-in popup (MioAuthDialog); signing in flips isSignedIn, which fires the
 * page's own auto-build (ProductLiveBuild on /c/<slug>, CategoryLiveBuild on
 * /c/list/<slug>): the full live build runs with the normal building screen and
 * lands back on the same page, fresh. The ribbon never navigates itself.
 *
 * Renders on the client only, after Clerk has loaded, so:
 *   - the server HTML is identical for everyone (bots, Google, signed-in),
 *   - signed-in visitors never see it (their stale visit auto-runs the full
 *     build instead, see ProductLiveBuild / CategoryLiveBuild),
 *   - it is inert while NEXT_PUBLIC_LIVE_PRICES_ENABLED is off.
 *
 * Copy is the approved mockup, verbatim.
 */

import { useState } from "react";

import { useRouter } from "next/navigation";
import { useUser } from "@clerk/nextjs";

import { MioAuthDialog } from "@/components/auth/MioAuthDialog";
import type { AuthMode } from "@/components/auth/useAuthFlow";
import { LIVE_PRICES_ENABLED } from "@/lib/flags";

interface Props {
  /** The raw query (product name or category); the ribbon renders only when
   *  it is non-empty, mirroring the auto-build components' guard. */
  query: string;
  isStale: boolean;
}

export function FreshnessRibbon({ query, isStale }: Props) {
  const { isSignedIn, isLoaded } = useUser();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<AuthMode>("sign-up");

  const q = (query ?? "").trim();

  // Signed-out + stale only, behind the flag, once Clerk knows who this is.
  if (!LIVE_PRICES_ENABLED || !isLoaded || isSignedIn || !isStale || !q) {
    return null;
  }

  function handleAuthenticated() {
    // Signing in IS the trigger: isSignedIn flips true and the page's auto-build
    // component (ProductLiveBuild on /c/<slug>, CategoryLiveBuild on /c/list)
    // runs the full live build for its own page type and lands back on it fresh.
    // The ribbon itself never navigates, so a category can never be mis-routed
    // into a product search (the 2026-08-23 bug). router.refresh() just
    // re-renders the server page for the new auth state.
    router.refresh();
  }

  return (
    <>
      {/* max-width 660 + mx-auto matches the search field (SearchAgainBar /
          HomeSearchInput) so the ribbon lines up with it on every page. */}
      <div
        className="relative mx-auto mb-4 flex w-full flex-col gap-3 overflow-hidden rounded-2xl border border-border bg-secondary p-4 pl-5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        style={{ maxWidth: 660 }}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-accent"
        />
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[10px] border"
            style={{
              background: "var(--amber-chip-bg)",
              borderColor: "var(--amber-chip-border)",
              color: "var(--amber-chip-text)",
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7.5V12l3 2" />
            </svg>
          </div>
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold leading-tight tracking-[-0.01em] text-foreground">
              Retailers change prices daily
            </h3>
            <p className="text-[13.5px] leading-snug text-muted-foreground">
              Sign in to see today&apos;s live prices at every store.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex flex-none items-center justify-center gap-2 rounded-xl bg-accent px-[17px] py-[11px] text-sm font-semibold text-accent-foreground shadow-sm transition hover:-translate-y-px hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[hsl(var(--ring))]"
        >
          <svg
            viewBox="0 0 24 24"
            width="15"
            height="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <path d="M21 3v5h-5" />
          </svg>
          See live prices
        </button>
      </div>

      <MioAuthDialog
        open={open}
        mode={mode}
        onOpenChange={setOpen}
        onModeChange={setMode}
        onAuthenticated={handleAuthenticated}
      />
    </>
  );
}
